import "./SectionNode.less";

import { notification, Icon, Spin } from "antd";
import React from "react";

import { OrderedSectionApi, SectionApi, SectionTemplateApi, SectionVersionApi } from "lib/api";
import { CancellationToken } from "lib/api/base";
import { ComponentTemplate, MultipleItemRequirement, OrderedSection, RequirementItem, Section, SectionTemplate, SectionVersion, SingleItemRequirement } from "lib/data/model";
import { IconType } from "lib/data/model/dictionaries/IconTypes";
import { StateService } from "lib/services";

import { AddNewButton, ButtonStateSwitch, MinimizeButton } from "components/general/StandardButtons";
import { MiniTemplateCard } from "components/general/TemplateCard";
import { CSSTransition } from "react-transition-group";
import { AddNode } from "../AddNode";
import { NodeContent } from "./NodeContent";

interface ISectionNodeProps {
	section: Section;
	parentSection?: Section;
	canExpand?: boolean;
	onDelete?: (child: Section) => Promise<boolean>;
}

interface ISectionNodeState {
	canExpand: boolean;
	isExpanded: boolean;
	buttonSelected: "editing-title" | "add-button" | "select-template" | "loading";
	templateRequirementItem?: RequirementItem<SectionTemplate>;
	isLoading: boolean;
	isLoaded: boolean;
	isDeleted: boolean;
}

export class SectionNode extends React.Component<ISectionNodeProps, ISectionNodeState> {

	private apiCancellationToken: CancellationToken = new CancellationToken();


	private section!: Section;

	private sectionTemplates: Array<RequirementItem<SectionTemplate>> = [];

	public constructor(props: ISectionNodeProps) {

		super(props);

		this.state = {
			canExpand: (this.props.canExpand === undefined || this.props.canExpand ? true : false),
			isExpanded: (this.props.canExpand !== undefined && !this.props.canExpand ? true : false),
			buttonSelected: "add-button",
			isLoading: true,
			isLoaded: false,
			isDeleted: false
		};
	}

	public async componentWillMount() {

		// Get Current Section
		const section = await SectionApi.getSection(this.props.section.id!, undefined);

		// Check Section
		if (section !== undefined) {

			// Set Section
			this.section = section;

			// Get Section Template
			const template = await SectionApi.getTemplate(this.section, true);

			// Check Section Template
			if (template !== undefined) {

				// Store Section Template
				this.section.template = template;

				// Get Current Section Versions
				this.section.published = await SectionVersionApi.getPublished(this.props.section);
				this.section.draft = await SectionVersionApi.getDraft(this.props.section);

				// Check Published
				if (this.section.published !== undefined) {

					// Get Published's Sections
					this.section.published.sections = await OrderedSectionApi.getAll(this.section, this.section.published);
				}

				// Check Draft
				if (this.section.draft !== undefined) {

					// Get Draft's Sections
					this.section.draft.sections = await OrderedSectionApi.getAll(this.section, this.section.draft);
				}

				// Check For Either Published or Draft
				if (this.section.published !== undefined || this.section.draft !== undefined) {

					// Process Section
					this.processTemplates();

					// Indicate Section Loaded
					StateService.setCancelableState(() => this.setState({
						canExpand: (this.props.canExpand === undefined || this.props.canExpand ? true : false),
						isExpanded: (this.props.canExpand !== undefined && !this.props.canExpand ? true : false),
						isLoading: false,
						isLoaded: true
					}), this.apiCancellationToken);

					// Leave Method
					return;
				}
			}
		}

		// Indicate Draft NOT Loaded
		StateService.setCancelableState(() => this.setState({ isLoading: false, isLoaded: false }), this.apiCancellationToken);
	}

	public componentWillReceiveProps(newProps: ISectionNodeProps) {

		// Check Expandability
		if (newProps.canExpand !== this.props.canExpand) {

			// Set New Values
			StateService.setCancelableState(() => this.setState({
				canExpand: (newProps.canExpand === undefined || newProps.canExpand ? true : false),
				isExpanded: (newProps.canExpand !== undefined && !newProps.canExpand ? true : false),
			}), this.apiCancellationToken);
		}
	}

	public componentWillUnmount() {

		// Cancel Outstanding API Calls
		this.apiCancellationToken.cancel();
	}

	public render() {

		if (this.state.isLoading) {

			return (
				<div className="new-wrapper">
					<Spin />
				</div>
			);
		}
		else if (!this.state.isLoaded) {

			return (
				<div className="new-wrapper">
					Error
				</div>
			);
		}

		// Initialize Expand Icon
		let expansionIcon: React.ReactNode = null;

		// Check Whether Children Can Expand
		if (this.state.canExpand) {

			// Check If Expanded
			if (this.state.isExpanded) {

				// Set Expansion Icon
				expansionIcon = <Icon onClick={this.collapseChildren} className="tree-indicator" type="minus-square" />;
			}
			else {

				// Set Expansion Icon
				expansionIcon = <Icon onClick={this.expandChildren} className="tree-indicator" type="plus-square" />;
			}
		}

		return (
			<div className="section-node-wrapper">

				<section className="section-node">

					{expansionIcon}

					<NodeContent
						parentSection={this.props.parentSection}
						section={this.section}
						title={(this.section.published !== undefined ? this.section.published.name : this.section.draft!.name)}
						subTitle={this.section.template.name}
						onSave={this.onContentSave}
						onDelete={this.onDelete}
						canDelete={this.props.parentSection !== undefined}
						isDraft={this.section.published === undefined}
					/>

					<CSSTransition
						in={this.state.isExpanded}
						timeout={{
							enter: 5000,
							exit: 200,
						}}
						classNames="wipe-transition"
						unmountOnExit={true}
					>
						<div className="child-wrapper">
							<div className="child-nodes-wrapper">
								{this.section.draft!.sections.sort(OrderedSection.sort).map((orderedSection) => (

									<SectionNode
										onDelete={this.onChildDelete}
										key={orderedSection.item.id!}
										parentSection={this.section}
										section={orderedSection.item}
									/>
								))}
							</div>

							<div className="new-wrapper">

								{/* On */}
								{this.sectionTemplates.length > 0
									?
									<ButtonStateSwitch on="add-button" in={this.state.buttonSelected}>
										<AddNewButton onClick={this.showAddNew} />
									</ButtonStateSwitch>
									:
									<p className="node-warning">The {this.section.template.name} template has no child sections</p>
								}

								<ButtonStateSwitch on="loading" in={this.state.buttonSelected}>
									<Spin />
								</ButtonStateSwitch>

								<ButtonStateSwitch on="editing-title" in={this.state.buttonSelected}>
									<AddNode
										isActive={this.state.buttonSelected === "editing-title"}
										closeEdit={this.hideAddNew}
										onSave={this.onChildSave}
									/>
								</ButtonStateSwitch>

								<ButtonStateSwitch className="select-template" on="select-template" in={this.state.buttonSelected}>
									<div className="mini-card-wrapper">

										<MinimizeButton onClick={() => StateService.setCancelableState(() => this.setState({ buttonSelected: "add-button" }), this.apiCancellationToken)} />

										{this.sectionTemplates.map((requirementItem, key) => (

											<MiniTemplateCard
												key={key}
												onClick={() => this.onChildTemplateSelected(requirementItem)}
												selected={this.state.templateRequirementItem ? this.state.templateRequirementItem.id === requirementItem.item.id : false}
												showCheckboxes={false}
												title={requirementItem.item.name}
												iconType={IconType.get(requirementItem.item.$type)}
												className="mini-card"
											/>
										))}

									</div>
								</ButtonStateSwitch>
							</div>
						</div>
					</CSSTransition>
				</section>
			</div>
		);
	}

	private onDelete = async (section: Section) => {

		// Indicate Loading Is Occurring
		StateService.setCancelableState(() => this.setState({ isLoading: true, canExpand: false }), this.apiCancellationToken);

		// Check For Parent's Delete Handler
		if (this.props.onDelete) {

			// Execute Parent's Child Save Handler
			const isDeleted = await this.props.onDelete(section);

			if (isDeleted === true) {

				notification.success({
					message: "Successfully deleted record"
				});
			}
			else {

				notification.error({
					message: "Failed to delete record"
				});
			}
		}

		// Indicate Loading Is NOT Occurring
		StateService.setCancelableState(() => this.setState({ isLoading: false }), this.apiCancellationToken);
	}

	private onContentSave = async (name: string) => {

		// Update Draft Name
		this.section.draft!.name = name;

		// Save Draft
		this.section.draft = await SectionVersionApi.updateDraft(this.section, this.section.draft!);
	}

	private onChildDelete = async (child: Section): Promise<boolean> => {

		// Locate Child In Draft
		const foundChildIndex = this.section.draft!.sections.findIndex((existingOrderedSection) => existingOrderedSection.item.id === child.id);

		// Check Found Child
		if (foundChildIndex !== -1) {

			// Remove Child From Draft
			this.section.draft!.sections.splice(foundChildIndex, 1);

			// Update Draft
			const updatedDraft = await SectionVersionApi.updateDraft(this.section, this.section.draft!);

			// Check Updated Draft
			if (updatedDraft !== undefined) {

				// Store Updated Draft
				this.section.draft = updatedDraft;

				// Check For Draft-Only Child
				if (child.published === undefined && child.id !== undefined) {

					// Delete Draft-Only Child
					await SectionApi.deleteSection(child.id);
				}

				// Refresh Component
				this.forceUpdate();

				// Return Success
				return true;
			}
		}

		// Return False
		return false;
	}

	private processTemplates = () => {

		// Local variable that will store all section template requirements for this sections Template
		const sectionTemplates: Array<RequirementItem<SectionTemplate>> = [];

		// Loop through all the requirements
		this.section.template.requirements.forEach((requirement) => {

			switch (requirement.$type) {

				case SingleItemRequirement.$type.ComponentTemplate:

					// Local variable to imply the SingleItemRequirement<ComponentTemplate> we just checked for
					const singleItemRequirement = requirement as SingleItemRequirement<ComponentTemplate>;

					// Check to see if it is a section template requirement
					if (singleItemRequirement.item.item.$type === SectionTemplate.$type) {

						// Push requirement item onto array
						sectionTemplates.push(singleItemRequirement.item as RequirementItem<SectionTemplate>);
					}

					break;

				case MultipleItemRequirement.$type.ComponentTemplate:

					// Local variable to imply the MultipleItemRequirement<ComponentTemplate> we just checked for
					const multipleItemRequirement = requirement as MultipleItemRequirement<ComponentTemplate>;

					multipleItemRequirement.items.forEach((requirementItem) => {

						// Check to see if it is a section template requirement
						if (requirementItem.item.$type === SectionTemplate.$type) {

							// Push requirement item onto array
							sectionTemplates.push(requirementItem as RequirementItem<SectionTemplate>);
						}
					});

					break;

				default:

					break;
			}

			// Set member section templates up to be our newly created sectionTemplates array
			this.sectionTemplates = sectionTemplates;
		});
	}

	private expandChildren = () => {

		if (!this.state.isExpanded) {

			StateService.setCancelableState(() => this.setState({ isExpanded: true }), this.apiCancellationToken);
		}
	}

	private collapseChildren = () => {

		if (this.state.isExpanded && (this.props.canExpand === undefined || !this.props.canExpand)) {

			// Collapse Children
			StateService.setCancelableState(() => this.setState({ isExpanded: false }), this.apiCancellationToken);
		}
	}

	private showAddNew = () => {

		StateService.setCancelableState(() => this.setState({ buttonSelected: "select-template" }), this.apiCancellationToken);
	}

	private hideAddNew = () => {

		StateService.setCancelableState(() => this.setState({ templateRequirementItem: undefined }), this.apiCancellationToken);
		StateService.setCancelableState(() => this.setState({ buttonSelected: "add-button" }), this.apiCancellationToken);
	}

	private onChildTemplateSelected = (requirementItem: RequirementItem<SectionTemplate>) => {

		// Set Child Template
		StateService.setCancelableState(() => this.setState({ templateRequirementItem: requirementItem }), this.apiCancellationToken);

		// Move To Next Child Add Step
		StateService.setCancelableState(() => this.setState({ buttonSelected: "editing-title" }), this.apiCancellationToken);
	}

	private onChildSave = async (name: string) => {

		// Store Requirement Item
		const requirementItem = this.state.templateRequirementItem;

		// Check For Selected & Saved Template
		if (requirementItem !== undefined
			&& requirementItem.id !== undefined
			&& requirementItem.item.id !== undefined) {

			// Indicate Child Is Saving
			StateService.setCancelableState(() => this.setState({ buttonSelected: "loading" }), this.apiCancellationToken);

			// Get Complete Template Record
			const completeTemplate = await SectionTemplateApi.getSectionTemplateById(requirementItem.item.id, true);

			// Check Complete Template
			if (completeTemplate) {

				// Construct New Draft Section Version
				const newSectionVersion: SectionVersion = new SectionVersion(name);

				// Construct New Section
				const newSection: Section = new Section(completeTemplate, newSectionVersion);

				// Construct New Ordered Section
				const newOrderedSection: OrderedSection = new OrderedSection(requirementItem.initialOrder, newSection);

				// Create New Ordered Section On Draft
				const orderedSection = await OrderedSectionApi.create(this.section, newOrderedSection);

				// Check New Ordered Section
				if (orderedSection !== undefined) {

					// Add New Ordered Section To Draft (UI)
					this.section.draft!.sections.push(orderedSection);

					// this.childSections.push(orderedSection.section);

					notification.success({
						message: "Successfully Saved Record"
					});
				}
				else {

					notification.success({
						message: "Failed to Save Record"
					});
				}

				// Indicate Saving Is Complete
				StateService.setCancelableState(() => this.setState({ buttonSelected: "add-button", templateRequirementItem: undefined }), this.apiCancellationToken);

				// this.Sections.push(newSection);
				// console.log(newOrderedSection);
				// this.props.section.draft!.sections.push(newOrderedSection);
				// StateService.setState(() => this.setState({ buttonSelected: "add-button", template: null, awaitingUpdate: true }), this.apiCancellationToken);
				// this.hardUpdate(() => { }, this.props.section.draft);
			}
			else {

				// Indicate Saving Is Complete
				StateService.setCancelableState(() => this.setState({ buttonSelected: "add-button" }), this.apiCancellationToken);
			}
		}
	}
}
